import React from "react";
import { Edit, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Header, Field, IconBtn, Modal } from "@/pages/admin/AdminBlog";

const emptyForm = { label: "", url: "", platform: "android", version: "", active: true };

export default function AdminDownloads() {
  const [items, setItems] = React.useState([]);
  const [form, setForm] = React.useState(emptyForm);
  const [editingId, setEditingId] = React.useState(null);
  const [show, setShow] = React.useState(false);

  const load = async () => setItems((await api.get("/admin/downloads")).data);
  React.useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editingId) await api.put(`/admin/downloads/${editingId}`, form);
      else await api.post("/admin/downloads", form);
      toast.success("Saved"); setShow(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/downloads/${id}`); load(); };

  return (
    <div data-testid="admin-downloads">
      <Header title="Download Links" onAdd={() => { setForm(emptyForm); setEditingId(null); setShow(true); }} addLabel="New Link" />
      <div className="mt-8 card-luxury overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5">
            <tr className="text-left text-xs uppercase tracking-widest text-slate-400">
              <th className="px-5 py-3">Label</th><th className="px-5 py-3">Platform</th>
              <th className="px-5 py-3">URL</th><th className="px-5 py-3">Version</th>
              <th className="px-5 py-3">Status</th><th className="px-5 py-3 w-32"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {items.map((d) => (
              <tr key={d.id}>
                <td className="px-5 py-3 text-white">{d.label}</td>
                <td className="px-5 py-3 text-slate-400">{d.platform}</td>
                <td className="px-5 py-3 text-sky-400 truncate max-w-xs">{d.url}</td>
                <td className="px-5 py-3 text-slate-400">{d.version}</td>
                <td className="px-5 py-3 text-xs">{d.active ? <span className="text-emerald-400">Active</span> : <span className="text-slate-500">Inactive</span>}</td>
                <td className="px-5 py-3 text-right">
                  <IconBtn onClick={() => { setForm({ ...d }); setEditingId(d.id); setShow(true); }}><Edit className="w-3.5 h-3.5" /></IconBtn>
                  <IconBtn onClick={() => remove(d.id)} variant="danger"><Trash2 className="w-3.5 h-3.5" /></IconBtn>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500 text-sm">No links yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {show && (
        <Modal title={editingId ? "Edit link" : "New link"} onClose={() => setShow(false)}>
          <form onSubmit={save} className="space-y-4">
            <Field label="Label"><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} required /></Field>
            <Field label="URL"><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} required /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Platform">
                <select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} className="w-full bg-[#0B1221] border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-sky-500 outline-none">
                  {["android", "ios", "apk"].map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
              <Field label="Version"><Input value={form.version || ""} onChange={(e) => setForm({ ...form, version: e.target.value })} /></Field>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <span className="text-slate-300">Active</span>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShow(false)} className="btn-ghost">Cancel</button>
              <button type="submit" className="btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
